import { isMedianShellUA } from "@/lib/platform";

// #1639 — the SAME matcher SocialAuthButtons and the platform detector use
// (`median` OR `gonative`, case-insensitive), so a caller that decides to
// route through this bridge and the bridge itself can never disagree about
// whether they are inside the shell. Real traffic has only ever carried a
// lowercase `median` UA (measured Sep 11 2026, see platform.ts); hygiene.
const isMedian = () =>
  typeof navigator !== "undefined" && isMedianShellUA(navigator.userAgent);

/**
 * Opens a URL in a genuinely external browser context — never same-window
 * navigation, never Median's in-app WKWebView. Any link that hands off out
 * of the app (Amazon, Kroger, a calendar link, an invite link, Stripe, the
 * App Store, Siri Shortcuts, anything on someone else's domain) must go
 * through this, not window.open/location.href directly — same-window nav
 * inside Median replaces the app itself and strands the user with no way
 * back in.
 *
 * Fallback chain proven across Pro.tsx's checkout links, useGoogleCalendar's
 * OAuth connect, and the 2026-07-19 Siri shortcut fix (see
 * feedback_median_external_links memory / commit history — a custom
 * shortcuts:// URL scheme was tried first and shipped broken; the actual
 * fix was routing the plain URL through this same bridge instead):
 * 1. Median's median.window.open(url, "external") bridge
 * 2. The median://window/open?...&mode=external URL scheme
 * 3. Plain location.href (last resort inside Median)
 * 4. Outside Median: window.open in a new tab
 */
export function openExternal(url: string): void {
  if (isMedian()) {
    try {
      const median = (window as any).median;
      if (median?.window?.open) {
        median.window.open(url, "external");
        return;
      }
    } catch { /* fall through */ }
    try {
      window.location.href = `median://window/open?url=${encodeURIComponent(url)}&mode=external`;
      return;
    } catch { /* fall through */ }
    window.location.href = url;
    return;
  }
  window.open(url, "_blank", "noopener,noreferrer");
}
